import { useState, useEffect } from "react";
import { Navigate, Outlet, useNavigate } from "react-router";
import { useAppSelector } from "../../store/hooks";
import { getSocket } from "../../realtime/socket";
import { IncomingCallModal } from "./IncomingCallModal";
import CameraPermissionModal from "./CameraPermissionModal";
import { assignListener, declineSession } from "../../api/sessions";
import { subscribePush } from "../../api/users";
import { toast } from "sonner";

const VAPID_PUBLIC_KEY =
  "BGCnfFrClZLYGmckxNZlTZbNT8wfEhUbxDPD-PI-sKzRAXkGd473re-pIi94iAnkkRrk4twyVZAk8RNxl70dG6s";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

interface PrivateRouteProps {
  allowedRole?: "talking" | "listener" | "admin";
}

const PrivateRoute = ({ allowedRole }: PrivateRouteProps) => {
  const { user, token, initialized, loading } = useAppSelector(
    (state) => state.auth,
  );

  const [incomingCall, setIncomingCall] = useState<any>(null);
  const navigate = useNavigate();

  // Handle Web Push registration for listeners
  useEffect(() => {
    if (!token || !user || user.role !== "listener") return;

    const setupPushNotifications = async () => {
      try {
        if (
          typeof window === "undefined" ||
          !("serviceWorker" in navigator) ||
          !("PushManager" in window)
        ) {
          console.warn("Push notifications are not supported in this browser.");
          return;
        }

        // Request permission if not already granted/denied
        if (Notification.permission === "default") {
          await Notification.requestPermission();
        }

        if (Notification.permission !== "granted") {
          console.warn("Notification permission not granted.");
          return;
        }

        // Register or get registered Service Worker
        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );

        let subscription = await registration.pushManager.getSubscription();
        const convertedVapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

        if (subscription) {
          // Check if applicationServerKey matches our active VAPID key
          const currentKey = subscription.options.applicationServerKey;
          if (currentKey) {
            const currentKeyUint8 = new Uint8Array(currentKey);
            let matches = currentKeyUint8.length === convertedVapidKey.length;
            if (matches) {
              for (let i = 0; i < convertedVapidKey.length; i++) {
                if (currentKeyUint8[i] !== convertedVapidKey[i]) {
                  matches = false;
                  break;
                }
              }
            }
            if (!matches) {
              console.log(
                "VAPID key mismatch/change detected. Re-subscribing...",
              );
              await subscription.unsubscribe();
              subscription = null;
            }
          } else {
            subscription = null;
          }
        }

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedVapidKey,
          });
        }

        // Must call .toJSON() to serialize standard Web Push properties (endpoint, keys)
        const subscriptionJSON = subscription.toJSON();
        console.log("Registered Push Subscription:", subscriptionJSON);
        await subscribePush(subscriptionJSON);
      } catch (err) {
        console.error("Failed to setup push notifications:", err);
      }
    };

    setupPushNotifications();

    window.addEventListener(
      "notification-permission-granted",
      setupPushNotifications,
    );
    return () => {
      window.removeEventListener(
        "notification-permission-granted",
        setupPushNotifications,
      );
    };
  }, [token, user]);

  // Clean up notifications on tab unload/close or logout (unmount)
  useEffect(() => {
    if (!token || !user || user.role !== "listener") return;

    const handleUnload = async () => {
      try {
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            const notifications = await registration.getNotifications();
            notifications.forEach((notification) => {
              if (notification.tag && notification.tag.startsWith("call:")) {
                console.log(
                  "Closing active notification on unload/unmount:",
                  notification.tag,
                );
                notification.close();
              }
            });
          }
        }
      } catch (err) {
        console.error("Failed to clear notifications on unload:", err);
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      handleUnload();
    };
  }, [token, user]);

  useEffect(() => {
    if (!token || !user || user.role !== "listener") return;

    const socket = getSocket(token);
    const onIncoming = (payload: any) => {
      // Don't show if already in a session
      if (window.location.pathname.includes("/session/active")) return;

      console.log("Global call:incoming observed:", payload);
      setIncomingCall(payload);
    };

    const onCallUnavailable = (data: { sessionId: string }) => {
      console.log("Global call:unavailable observed:", data);
      setIncomingCall((prev: any) => {
        if (prev && String(prev.sessionId) === String(data.sessionId)) {
          return null;
        }
        return prev;
      });
    };

    socket.on("call:incoming", onIncoming);
    socket.on("call:unavailable", onCallUnavailable);
    return () => {
      socket.off("call:incoming", onIncoming);
      socket.off("call:unavailable", onCallUnavailable);
    };
  }, [token, user]);

  const handleAccept = async () => {
    if (!incomingCall || !token) return;

    // 1. Emit accept to socket
    getSocket(token).emit("call:accept", {
      sessionId: incomingCall.sessionId,
      listenerName: user?.name || "A listener",
      listenerId: user?.id,
    });

    // 2. Call API to assign officially
    try {
      const res = await assignListener(incomingCall.sessionId.toString());
      const session = "session" in res ? res.session : res;

      // 3. Clear and navigate only if successful
      const sid = session?.id || incomingCall.sessionId;
      setIncomingCall(null);
      toast.success("Session started!", {
        description: "Navigating to your session…",
      });
      navigate(`/session/active?sessionId=${sid}`);
    } catch (e) {
      console.error("Assign listener error:", e);
      toast.error("Could not start session", {
        description: "The session may have already been taken.",
      });
      setIncomingCall(null);
    }
  };

  const handleReject = () => {
    if (!incomingCall || !token) return;
    getSocket(token).emit("call:reject", { sessionId: incomingCall.sessionId });
    declineSession(incomingCall.sessionId.toString()).catch((err) =>
      console.error("Decline API error:", err)
    );
    setIncomingCall(null);
  };

  if (token && !initialized && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-purple-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRole && user && user.role !== allowedRole) {
    // Redirect to their respective dashboard if role doesn't match
    let redirectPath = "/dashboard";
    if (user.role === "listener") redirectPath = "/listener/dashboard";
    if (user.role === "admin") redirectPath = "/admin/dashboard";
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <>
      <Outlet />
      <CameraPermissionModal />
      {incomingCall && (
        <IncomingCallModal
          incomingCall={incomingCall}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}
    </>
  );
};

export default PrivateRoute;
